/**
 * MODULE: SystemHealth.jsx
 * PURPOSE: Admin "System Health" tab — the Phase 3 auto-diagnosis dashboard.
 *          Shows PASS/FAIL/DEGRADED for every backend subsystem (DB, LLM tiers,
 *          Stripe, RSS ingestion, auth, route modules) with the exact file to
 *          open, plus recent structured incidents with a copy-paste "repair
 *          prompt" you can hand to any coding agent.
 * PUBLIC API: <SystemHealth />
 * DEPENDS ON: GET /api/system/health-deep, GET /api/admin/errors/recent
 * CALLED BY: pages/Admin.jsx ("System Health" tab)
 * LAST REVIEWED: 2026-06-30
 */
import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Copy, Check } from "lucide-react";
import { SectionLabel } from "./atoms";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  PASS:     { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  DEGRADED: { icon: AlertTriangle, color: "text-amber-600",  bg: "bg-amber-50 border-amber-200" },
  FAIL:     { icon: XCircle,       color: "text-red-600",    bg: "bg-red-50 border-red-200" },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.DEGRADED;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full border font-mono text-[10px] tracking-[0.08em] uppercase ${cfg.bg} ${cfg.color}`}>
      <Icon size={12} strokeWidth={2} />
      {status}
    </span>
  );
};

const ModuleCard = ({ name, data }) => (
  <div
    className={`border rounded-[8px] p-4 ${STATUS_CFG[data.status]?.bg || "border-[var(--hairline)]"}`}
    data-testid={`health-module-${name}`}
  >
    <div className="flex items-center justify-between mb-1.5">
      <span className="font-sans text-[13.5px] font-semibold text-[var(--ink)] capitalize">{name.replace(/_/g, " ")}</span>
      <StatusPill status={data.status} />
    </div>
    <p className="font-sans text-[12.5px] text-[var(--ink-2)] leading-[1.5]">{data.detail}</p>
    {data.file && <p className="font-mono text-[10px] text-[var(--muted)] mt-1.5">{data.file}</p>}
    {data.repair_hint && (
      <p className="font-mono text-[10.5px] text-[var(--crimson)] mt-1.5">Hint: {data.repair_hint}</p>
    )}
  </div>
);

const IncidentRow = ({ inc }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border-b border-[var(--hairline)] py-3 flex items-start justify-between gap-4" data-testid="health-incident-row">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] font-semibold text-[var(--ink)]">{inc.module}.{inc.function}</span>
          <span className="font-mono text-[9.5px] text-[var(--muted)]">{new Date(inc.timestamp).toLocaleString()}</span>
        </div>
        <p className="font-sans text-[12.5px] text-[var(--ink-2)] mt-0.5 truncate">{inc.detail}</p>
      </div>
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(inc.repair_prompt); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-[var(--hairline)] font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink-2)] hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors"
        data-testid="health-copy-repair-prompt"
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? "Copied" : "Copy repair prompt"}
      </button>
    </div>
  );
};

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      fetch(`${BASE}/api/system/health-deep`, { credentials: "include" }).then((r) => r.json()),
      fetch(`${BASE}/api/admin/errors/recent?limit=20`, { credentials: "include" }).then((r) => r.json()),
    ])
      .then(([h, e]) => { setHealth(h); setIncidents(e.incidents || []); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="py-8" data-testid="admin-system-health">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <SectionLabel>Auto-diagnosis</SectionLabel>
            <h2 className="font-serif text-[26px] text-[var(--ink)] mt-1">System Health</h2>
          </div>
          <div className="flex items-center gap-3">
            {health && (
              <span className="font-mono text-[10px] text-[var(--muted)]">
                Last checked {new Date(health.checked_at).toLocaleTimeString()}
              </span>
            )}
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--hairline)] font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink-2)] hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors"
              data-testid="health-refresh-btn"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {loading && !health && <p className="font-sans text-[13px] text-[var(--muted)]">Running diagnostics…</p>}
        {error && <p className="font-sans text-[13px] text-red-600">Could not reach the health-deep endpoint.</p>}

        {health && (
          <>
            <div className="mb-6">
              <span className="font-sans text-[13px] text-[var(--muted)] mr-2">Overall:</span>
              <StatusPill status={health.overall} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10" data-testid="health-modules-grid">
              {Object.entries(health.modules).map(([name, data]) => (
                <ModuleCard key={name} name={name} data={data} />
              ))}
            </div>
          </>
        )}

        <div>
          <h3 className="font-sans text-[14px] font-semibold text-[var(--ink)] mb-3">
            Recent incidents {incidents.length > 0 && `(${incidents.length})`}
          </h3>
          {incidents.length === 0 ? (
            <p className="font-sans text-[13px] text-[var(--muted)]">No structured incidents logged yet — good sign.</p>
          ) : (
            <div data-testid="health-incidents-list">
              {incidents.map((inc, i) => <IncidentRow key={i} inc={inc} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
